import { TaskRecord } from '../../containers/tasks-table/tasks-table.type';
import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  AdherenceLog,
  AdherenceLogEventType,
  AgentEventLog,
  AgentEventTypes,
  AgentFinancialResponse,
  AgentSession,
  AgentSource,
} from './events-client.type';

const axiosInstance = AxiosInstance();
const adherenceLogBaseURL = appConfig.VITE_APP_API_URL + '/api/adherence-logs/';
const agentEventLogBaseURL = appConfig.VITE_APP_API_URL + '/api/agent-event-logs/';
/**
 * Fetches adherence logs for a given organization and event type
 *
 * @param {string} organizationId - The ID of the organization to fetch adherence logs for
 * @param {AdherenceLogEventType[]} eventTypes - The types of events to fetch adherence logs for
 * @returns {Promise<AdherenceLog[]>} - A promise that resolves to the adherence logs for the organization and event type
 */
const getAdherenceLogs = (organizationId: string, eventTypes?: AdherenceLogEventType[]): Promise<AdherenceLog[]> => {
  const queryParams = new URLSearchParams();
  queryParams.set('organization_id', organizationId);
  if (eventTypes) {
    queryParams.set('event_types', eventTypes.join(','));
  }
  return axiosInstance
    .get(`${adherenceLogBaseURL}?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching adherence logs'));
};

/**
 * Fetches agent event logs with optional filtering
 *
 * @param {Object} params - The filtering parameters
 * @param {string} [params.sessionId] - Filter by session ID
 * @param {AgentEventTypes} [params.eventType] - Filter by event type
 * @param {AgentSource} [params.agentSource] - Filter by agent source
 * @param {string} [params.userId] - Filter by user ID
 * @param {string} [params.taskId] - Filter by task ID
 * @returns {Promise<AgentEventLog[]>} - A promise that resolves to the agent event logs
 */
const getAgentEventLogs = (params: {
  sessionId?: string;
  eventType?: AgentEventTypes;
  agentSource?: AgentSource;
  userId?: string;
  taskId?: string;
}): Promise<AgentEventLog[]> => {
  const queryParams = new URLSearchParams();

  if (params.sessionId) {
    queryParams.set('session_id', params.sessionId);
  }
  if (params.eventType) {
    queryParams.set('event_type', params.eventType);
  }
  if (params.agentSource) {
    queryParams.set('agent_source', params.agentSource);
  }
  if (params.userId) {
    queryParams.set('user_id', params.userId);
  }
  if (params.taskId) {
    queryParams.set('task_id', params.taskId);
  }

  return axiosInstance
    .get(`${agentEventLogBaseURL}?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching agent event logs'));
};

/**
 * Fetches agent event related tasks
 *
 * @param {Object} params - The filtering parameters
 * @param {string} params.portfolioId - Filter by portfolio ID
 * @param {string} params.projectId - Filter by project ID
 * @param {string} params.subprojectId - Filter by subproject ID
 * @param {string} params.startDate - Filter by start date
 * @param {string} params.endDate - Filter by end date
 * @returns {Promise<TaskRecord[]>} - A promise that resolves to the agent event related tasks
 */
const getAgentEventRelatedTasks = (params: {
  portfolioId?: string;
  projectId?: string;
  subprojectId?: string;
  startDate: string;
  endDate: string;
}): Promise<TaskRecord[]> => {
  const queryParams = new URLSearchParams();
  if (params.portfolioId) {
    queryParams.set('portfolio_id', params.portfolioId);
  }
  if (params.projectId) {
    queryParams.set('project_id', params.projectId);
  }
  if (params.subprojectId) {
    queryParams.set('subproject_id', params.subprojectId);
  }
  queryParams.set('start_date', params.startDate);
  queryParams.set('end_date', params.endDate);
  return axiosInstance
    .get(`${agentEventLogBaseURL}tasks/?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching agent event related tasks'));
};

/**
 * Fetches an agent event related task by ID
 *
 * @param {string} taskId - The ID of the task to fetch
 * @returns {Promise<TaskRecord>} - A promise that resolves to the agent event related task
 */
const getAgentEventRelatedTask = (taskId: string): Promise<TaskRecord> => {
  return axiosInstance
    .get(`${agentEventLogBaseURL}tasks/${encodeURIComponent(taskId)}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching agent event related task'));
};

/**
 * Fetches agent sessions for a given organization and date range
 *
 * @param {Object} params - The filtering parameters
 * @param {string} params.organizationId - The ID of the organization to fetch agent sessions for
 * @param {string} params.startDate - The start date of the date range
 * @param {string} params.endDate - The end date of the date range
 * @returns {Promise<AgentSession[]>} - A promise that resolves to the agent sessions for the organization and date range
 */
const getAgentSessions = (params: {
  organizationId: string;
  startDate: string;
  endDate: string;
}): Promise<AgentSession[]> => {
  const queryParams = new URLSearchParams();
  queryParams.set('organization_id', params.organizationId);
  queryParams.set('start_date', params.startDate);
  queryParams.set('end_date', params.endDate);
  return axiosInstance
    .get(`${agentEventLogBaseURL}sessions/?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching agent sessions'));
};

/**
 * Fetches agent financial data for a given organization and date range
 *
 * @param {Object} params - The filtering parameters
 * @param {string} params.organizationId - The ID of the organization to fetch agent financial data for
 * @param {string} params.startDate - The start date of the date range
 * @param {string} params.endDate - The end date of the date range
 * @returns {Promise<AgentFinancialResponse>} - A promise that resolves to the agent financial data for the organization and date range
 */
const getAgentFinancialData = (params: {
  organizationId: string;
  startDate: string;
  endDate: string;
}): Promise<AgentFinancialResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.set('organization_id', params.organizationId);
  queryParams.set('start_date', params.startDate);
  queryParams.set('end_date', params.endDate);
  return axiosInstance
    .get(`${agentEventLogBaseURL}costs/?${queryParams.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching agent financial data'));
};

export {
  getAdherenceLogs,
  getAgentEventLogs,
  getAgentEventRelatedTask,
  getAgentEventRelatedTasks,
  getAgentFinancialData,
  getAgentSessions,
};
